import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserPlus, Users, TrendingUp, QrCode, Copy, Download, Search,
  Phone, Mail, MoreVertical, Edit2, Trash2, RefreshCw, CheckCircle,
  UserCheck, UserX, ExternalLink, DollarSign, Award, Send, FileText,
  MessageSquare, Loader2, CheckSquare, Square, X, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import { api } from '@/utils/apiClient';

export default function SalesRepsTab() {
  const [reps, setReps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedRep, setSelectedRep] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareOptions, setShareOptions] = useState({
    sendSms: false,
    sendEmail: false
  });
  
  // Bulk selection state
  const [selectedRepIds, setSelectedRepIds] = useState(new Set());
  const [showBulkSendModal, setShowBulkSendModal] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, results: [] });
  const [bulkShareOptions, setBulkShareOptions] = useState({
    sendSms: true,
    sendEmail: true
  });
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [repsRes, statsRes] = await Promise.all([
        api.get('/sales-reps/list'),
        api.get('/sales-reps/stats/summary')
      ]);
      
      setReps(repsRes.data.reps || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch sales reps:', err);
      toast.error('Failed to load sales reps');
    }
    setLoading(false);
  };

  const createRep = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Name and phone are required');
      return;
    }
    
    setCreating(true);
    try {
      await api.post('/sales-reps/create', formData);
      toast.success('Sales rep created successfully');
      setShowCreateModal(false);
      setFormData({ name: '', phone: '', email: '', notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create sales rep');
    }
    setCreating(false);
  };

  const deleteRep = async (repId) => {
    if (!window.confirm('Are you sure you want to deactivate this sales rep?')) return;
    
    try {
      await api.delete(`/sales-reps/${repId}`);
      toast.success('Sales rep deactivated');
      fetchData();
    } catch (err) {
      toast.error('Failed to deactivate sales rep');
    }
  };

  const generateQRCode = async (rep) => {
    setSelectedRep(rep);
    const signupUrl = `${window.location.origin}/partner-signup?rep=${rep.code}`;
    
    try {
      const dataUrl = await QRCode.toDataURL(signupUrl, {
        width: 300,
        margin: 2,
        color: { dark: '#166534', light: '#ffffff' }
      });
      setQrCodeDataUrl(dataUrl);
      setShowQRModal(true);
    } catch (err) {
      toast.error('Failed to generate QR code');
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeDataUrl || !selectedRep) return;
    
    const link = document.createElement('a');
    link.download = `SalesRep-${selectedRep.code}-QR.png`;
    link.href = qrCodeDataUrl;
    link.click();
  };

  const copyLink = (rep) => {
    const signupUrl = `${window.location.origin}/partner-signup?rep=${rep.code}`;
    navigator.clipboard.writeText(signupUrl);
    toast.success('Link copied to clipboard');
  };

  const openShareModal = async (rep) => {
    setSelectedRep(rep);
    setShareOptions({
      sendSms: !!rep.phone,
      sendEmail: !!rep.email
    });
    
    // Generate QR code for preview
    const signupUrl = `${window.location.origin}/partner-signup?rep=${rep.code}`;
    try {
      const dataUrl = await QRCode.toDataURL(signupUrl, {
        width: 200,
        margin: 2,
        color: { dark: '#166534', light: '#ffffff' }
      });
      setQrCodeDataUrl(dataUrl);
    } catch (err) {
      console.error('Failed to generate QR code:', err);
    }
    
    setShowShareModal(true);
  };

  const shareMaterials = async () => {
    if (!selectedRep) return;
    if (!shareOptions.sendSms && !shareOptions.sendEmail) {
      toast.error('Please select at least one delivery method');
      return;
    }
    
    setSharing(true);
    try {
      const res = await api.post('/sales-reps/share-materials', {
        rep_id: selectedRep.id,
        send_sms: shareOptions.sendSms,
        send_email: shareOptions.sendEmail
      });
      
      const data = res.data;
      
      if (data.success) {
        const methods = [];
        if (shareOptions.sendSms) methods.push('SMS');
        if (shareOptions.sendEmail) methods.push('Email');
        toast.success(`Materials sent via ${methods.join(' & ')} to ${selectedRep.name}`);
        setShowShareModal(false);
      } else {
        // Partial success handling
        const results = data.results || {};
        if (results.sms?.success === false) {
          toast.error(`SMS failed: ${results.sms.error}`);
        }
        if (results.email?.success === false) {
          toast.error(`Email failed: ${results.email.error}`);
        }
        if (data.success) {
          toast.success('Some materials sent successfully');
          setShowShareModal(false);
        }
      }
    } catch (err) {
      console.error('Failed to share materials:', err);
      toast.error('Failed to send materials');
    }
    setSharing(false);
  };

  // Bulk selection functions
  const toggleSelectRep = (repId) => {
    setSelectedRepIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(repId)) {
        newSet.delete(repId);
      } else {
        newSet.add(repId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedRepIds.size === filteredReps.length) {
      setSelectedRepIds(new Set());
    } else {
      setSelectedRepIds(new Set(filteredReps.map(rep => rep.id)));
    }
  };

  const clearSelection = () => {
    setSelectedRepIds(new Set());
  };

  const getSelectedReps = () => {
    return filteredReps.filter(rep => selectedRepIds.has(rep.id));
  };

  const openBulkSendModal = () => {
    const selected = getSelectedReps();
    const hasPhone = selected.some(rep => rep.phone);
    const hasEmail = selected.some(rep => rep.email);
    
    setBulkShareOptions({
      sendSms: hasPhone,
      sendEmail: hasEmail
    });
    setBulkProgress({ current: 0, total: 0, results: [] });
    setShowBulkSendModal(true);
  };

  const sendBulkMaterials = async () => {
    const selected = getSelectedReps();
    if (selected.length === 0) return;
    if (!bulkShareOptions.sendSms && !bulkShareOptions.sendEmail) {
      toast.error('Please select at least one delivery method');
      return;
    }
    
    setBulkSending(true);
    setBulkProgress({ current: 0, total: selected.length, results: [] });
    
    const results = [];
    
    for (let i = 0; i < selected.length; i++) {
      const rep = selected[i];
      
      try {
        const res = await api.post('/sales-reps/share-materials', {
          rep_id: rep.id,
          send_sms: bulkShareOptions.sendSms && !!rep.phone,
          send_email: bulkShareOptions.sendEmail && !!rep.email
        });
        
        const data = res.data;
        results.push({
          rep,
          success: data.success,
          smsSuccess: data.results?.sms?.success,
          emailSuccess: data.results?.email?.success,
          error: !data.success ? (data.results?.sms?.error || data.results?.email?.error) : null
        });
      } catch (err) {
        results.push({
          rep,
          success: false,
          error: err.message
        });
      }
      
      setBulkProgress({ current: i + 1, total: selected.length, results: [...results] });
      
      // Small delay between requests to avoid rate limiting
      if (i < selected.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
    
    setBulkSending(false);
    
    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;
    
    if (successCount === results.length) {
      toast.success(`Materials sent to all ${successCount} sales reps!`);
      setShowBulkSendModal(false);
      clearSelection();
    } else if (successCount > 0) {
      toast.warning(`Sent to ${successCount} reps, ${failCount} failed`);
    } else {
      toast.error('Failed to send materials to any reps');
    }
  };

  const filteredReps = reps.filter(rep =>
    rep.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rep.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rep.phone?.includes(searchQuery)
  );

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-reps-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Sales Representatives</h2>
          <p className="text-muted-foreground">Track reps who recruit partners into the program</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateModal(true)} data-testid="add-sales-rep-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Add Sales Rep
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card p-5 rounded-xl border border-border"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-foreground">{stats.total_active_reps || 0}</p>
                <p className="text-sm text-muted-foreground">Active Sales Reps</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-card p-5 rounded-xl border border-border"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-foreground">{stats.total_attributed_partners || 0}</p>
                <p className="text-sm text-muted-foreground">Partners Enrolled</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card p-5 rounded-xl border border-border"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <UserX className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-foreground">{stats.total_unattributed_partners || 0}</p>
                <p className="text-sm text-muted-foreground">Self-Enrolled Partners</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-card p-5 rounded-xl border border-border"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Award className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                {stats.top_performing_rep ? (
                  <>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Top Performer</p>
                    <p className="text-lg font-bold text-foreground truncate">{stats.top_performing_rep.name}</p>
                    <p className="text-sm text-muted-foreground">{stats.top_performing_rep.partners_enrolled} partners</p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-bold text-foreground">—</p>
                    <p className="text-sm text-muted-foreground">Top Performer</p>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Search and Bulk Actions */}
      <div className="flex flex-col gap-4">
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, code, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="sales-rep-search"
            />
          </div>
        </div>

        {/* Bulk Action Bar */}
        <AnimatePresence>
          {selectedRepIds.size > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center">
                  <CheckSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-green-800 dark:text-green-300">
                    {selectedRepIds.size} sales rep{selectedRepIds.size !== 1 ? 's' : ''} selected
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Ready to send materials
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearSelection}
                  className="border-green-300 text-green-700 hover:bg-green-100"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
                <Button 
                  size="sm" 
                  onClick={openBulkSendModal}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="bulk-send-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Materials to All
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Sales Reps List */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-12 p-4">
                <Checkbox
                  checked={filteredReps.length > 0 && selectedRepIds.size === filteredReps.length}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-checkbox"
                />
              </th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Sales Rep</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Code</th>
              <th className="text-center p-4 text-sm font-medium text-muted-foreground">Partners Enrolled</th>
              <th className="text-center p-4 text-sm font-medium text-muted-foreground">Revenue Generated</th>
              <th className="text-center p-4 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredReps.map((rep) => (
              <tr 
                key={rep.id} 
                className={`hover:bg-muted/30 transition-colors ${selectedRepIds.has(rep.id) ? 'bg-green-50/50 dark:bg-green-900/10' : ''}`} 
                data-testid={`sales-rep-row-${rep.id}`}
              >
                <td className="p-4">
                  <Checkbox
                    checked={selectedRepIds.has(rep.id)}
                    onCheckedChange={() => toggleSelectRep(rep.id)}
                    data-testid={`select-rep-${rep.id}`}
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                      <span className="text-lg font-bold text-primary">{rep.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{rep.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Phone className="w-3 h-3" />
                        <span>{rep.phone}</span>
                        {rep.email && (
                          <>
                            <Mail className="w-3 h-3 ml-2" />
                            <span>{rep.email}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <code className="text-sm font-mono bg-muted px-2 py-1 rounded">{rep.code}</code>
                </td>
                <td className="p-4 text-center">
                  <span className="text-2xl font-bold text-foreground">{rep.total_partners_enrolled || 0}</span>
                </td>
                <td className="p-4 text-center">
                  <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                    ${(rep.total_revenue_generated || 0).toFixed(0)}
                  </span>
                </td>
                <td className="p-4 text-center">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    rep.status === 'active' 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  }`}>
                    {rep.status === 'active' ? <CheckCircle className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                    {rep.status}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openShareModal(rep)}
                      title="Share Materials"
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      data-testid={`share-btn-${rep.id}`}
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => generateQRCode(rep)}
                      title="Generate QR Code"
                      data-testid={`qr-btn-${rep.id}`}
                    >
                      <QrCode className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyLink(rep)}
                      title="Copy Signup Link"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteRep(rep.id)}
                      title="Deactivate"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredReps.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>{searchQuery ? 'No sales reps found' : 'No sales reps yet. Add one to get started!'}</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create Sales Rep Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Sales Representative</DialogTitle>
          </DialogHeader>
          <form onSubmit={createRep} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Smith"
                required
                data-testid="rep-name-input"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="9991234"
                required
                data-testid="rep-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email (optional)</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any notes about this sales rep..."
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating} data-testid="create-rep-submit">
                {creating ? 'Creating...' : 'Create Sales Rep'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sales Rep QR Code</DialogTitle>
          </DialogHeader>
          {selectedRep && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="font-semibold text-lg text-foreground">{selectedRep.name}</p>
                <code className="text-sm bg-muted px-2 py-1 rounded">{selectedRep.code}</code>
              </div>
              
              <div className="bg-white p-4 rounded-xl flex justify-center">
                {qrCodeDataUrl && (
                  <img src={qrCodeDataUrl} alt="QR Code" className="w-64 h-64" />
                )}
              </div>
              
              <p className="text-sm text-center text-muted-foreground">
                Partners who scan this QR code will be attributed to {selectedRep.name}
              </p>
              
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => copyLink(selectedRep)}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button className="flex-1" onClick={downloadQRCode}>
                  <Download className="w-4 h-4 mr-2" />
                  Download QR
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Materials Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-green-600" />
              Share Materials with Sales Rep
            </DialogTitle>
            <DialogDescription>
              Send personalized materials including QR code, signup link, and flyer directly to the sales rep.
            </DialogDescription>
          </DialogHeader>
          {selectedRep && (
            <div className="space-y-6">
              {/* Rep Info Card */}
              <div className="bg-muted/50 rounded-xl p-4 flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-white">{selectedRep.name?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-lg text-foreground">{selectedRep.name}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {selectedRep.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {selectedRep.phone}
                      </span>
                    )}
                    {selectedRep.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {selectedRep.email}
                      </span>
                    )}
                  </div>
                  <code className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded mt-1 inline-block">
                    {selectedRep.code}
                  </code>
                </div>
                {qrCodeDataUrl && (
                  <div className="hidden sm:block flex-shrink-0 bg-white p-2 rounded-lg">
                    <img src={qrCodeDataUrl} alt="QR" className="w-16 h-16" />
                  </div>
                )}
              </div>

              {/* Materials Preview */}
              <div>
                <h4 className="font-medium text-sm text-foreground mb-3">Materials included:</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-center">
                    <QrCode className="w-6 h-6 mx-auto mb-1 text-amber-600" />
                    <p className="text-xs font-medium text-amber-800 dark:text-amber-400">Partner Signup Link</p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
                    <FileText className="w-6 h-6 mx-auto mb-1 text-blue-600" />
                    <p className="text-xs font-medium text-blue-800 dark:text-blue-400">Printable Flyer</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                    <FileText className="w-6 h-6 mx-auto mb-1 text-green-600" />
                    <p className="text-xs font-medium text-green-800 dark:text-green-400">Sales Rep Guide</p>
                  </div>
                </div>
              </div>

              {/* Delivery Method Selection */}
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-foreground">Send via:</h4>
                
                <label 
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    shareOptions.sendSms 
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                      : 'border-border hover:border-muted-foreground/30'
                  } ${!selectedRep.phone ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Checkbox
                    checked={shareOptions.sendSms}
                    onCheckedChange={(checked) => setShareOptions(prev => ({ ...prev, sendSms: checked }))}
                    disabled={!selectedRep.phone}
                    data-testid="share-sms-checkbox"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-foreground">SMS Text Message</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedRep.phone 
                        ? `Send to ${selectedRep.phone}` 
                        : 'No phone number on file'}
                    </p>
                  </div>
                </label>

                <label 
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    shareOptions.sendEmail 
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                      : 'border-border hover:border-muted-foreground/30'
                  } ${!selectedRep.email ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Checkbox
                    checked={shareOptions.sendEmail}
                    onCheckedChange={(checked) => setShareOptions(prev => ({ ...prev, sendEmail: checked }))}
                    disabled={!selectedRep.email}
                    data-testid="share-email-checkbox"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Mail className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-foreground">Email</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedRep.email 
                        ? `Send to ${selectedRep.email}` 
                        : 'No email address on file'}
                    </p>
                  </div>
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowShareModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={shareMaterials} 
                  disabled={sharing || (!shareOptions.sendSms && !shareOptions.sendEmail)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="send-materials-btn"
                >
                  {sharing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Materials
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Send Materials Modal */}
      <Dialog open={showBulkSendModal} onOpenChange={(open) => {
        if (!bulkSending) setShowBulkSendModal(open);
      }}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-green-600" />
              Bulk Send Materials
            </DialogTitle>
            <DialogDescription>
              Send personalized materials to {selectedRepIds.size} selected sales rep{selectedRepIds.size !== 1 ? 's' : ''} at once.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Selected Reps Summary */}
            <div className="bg-muted/50 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">{selectedRepIds.size} Sales Reps Selected</p>
                  <p className="text-sm text-muted-foreground">
                    {getSelectedReps().filter(r => r.phone).length} with phone, {getSelectedReps().filter(r => r.email).length} with email
                  </p>
                </div>
              </div>
              
              {/* Preview list */}
              <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                {getSelectedReps().slice(0, 10).map(rep => (
                  <span key={rep.id} className="inline-flex items-center gap-1 bg-background px-2 py-1 rounded-full text-xs border">
                    <span className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-[10px]">
                      {rep.name?.charAt(0)?.toUpperCase()}
                    </span>
                    {rep.name}
                  </span>
                ))}
                {getSelectedReps().length > 10 && (
                  <span className="text-xs text-muted-foreground px-2 py-1">
                    +{getSelectedReps().length - 10} more
                  </span>
                )}
              </div>
            </div>

            {/* Delivery Options */}
            {!bulkSending && bulkProgress.current === 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-foreground">Delivery Methods:</h4>
                
                <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  bulkShareOptions.sendSms 
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                    : 'border-border hover:border-muted-foreground/30'
                }`}>
                  <Checkbox
                    checked={bulkShareOptions.sendSms}
                    onCheckedChange={(checked) => setBulkShareOptions(prev => ({ ...prev, sendSms: checked }))}
                    data-testid="bulk-sms-checkbox"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-foreground">SMS Text Message</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Send to {getSelectedReps().filter(r => r.phone).length} reps with phone numbers
                    </p>
                  </div>
                </label>

                <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  bulkShareOptions.sendEmail 
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                    : 'border-border hover:border-muted-foreground/30'
                }`}>
                  <Checkbox
                    checked={bulkShareOptions.sendEmail}
                    onCheckedChange={(checked) => setBulkShareOptions(prev => ({ ...prev, sendEmail: checked }))}
                    data-testid="bulk-email-checkbox"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Mail className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-foreground">Email</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Send to {getSelectedReps().filter(r => r.email).length} reps with email addresses
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* Progress Section */}
            {(bulkSending || bulkProgress.current > 0) && (
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">{bulkProgress.current} / {bulkProgress.total}</span>
                  </div>
                  <Progress value={(bulkProgress.current / bulkProgress.total) * 100} className="h-2" />
                </div>

                {/* Results */}
                {bulkProgress.results.length > 0 && (
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {bulkProgress.results.map((result, idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-center gap-3 p-2 rounded-lg text-sm ${
                          result.success 
                            ? 'bg-green-50 dark:bg-green-900/20' 
                            : 'bg-red-50 dark:bg-red-900/20'
                        }`}
                      >
                        {result.success ? (
                          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                        )}
                        <span className="flex-1 truncate">{result.rep.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {result.success ? 'Sent' : result.error || 'Failed'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-2">
              {!bulkSending && bulkProgress.current === 0 ? (
                <>
                  <Button variant="outline" onClick={() => setShowBulkSendModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={sendBulkMaterials}
                    disabled={!bulkShareOptions.sendSms && !bulkShareOptions.sendEmail}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="confirm-bulk-send-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send to {selectedRepIds.size} Reps
                  </Button>
                </>
              ) : bulkSending ? (
                <Button disabled className="bg-green-600">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending... ({bulkProgress.current}/{bulkProgress.total})
                </Button>
              ) : (
                <Button onClick={() => {
                  setShowBulkSendModal(false);
                  clearSelection();
                }}>
                  Done
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
